import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Check,
  Shield,
  Zap,
  TrendingUp,
  Users,
  CreditCard,
  ArrowRight,
  LayoutDashboard,
  Dumbbell,
  Bell,
  Search,
  UserCog,
  Calendar,
  Activity,
  X,
  BookOpen,
  FileSpreadsheet,
  Smartphone,
  BarChart2,
  ChevronRight,
  Star,
  Clock,
  Lock,
  Globe,
  Headphones,
} from 'lucide-react';
import { isMarketingSite, APP_URL } from '../lib/domains';

/**
 * SmartLink — uses React Router <Link> on app domain, full <a> on marketing domain.
 * On www.fitxero.com, /login → https://app.fitxero.com/login
 * On app.fitxero.com, /login → same-origin React Router nav
 */
const SmartLink = ({ to, children, className, ...props }) => {
  if (isMarketingSite) {
    return (
      <a href={`${APP_URL}${to}`} className={className} {...props}>
        {children}
      </a>
    );
  }
  return (
    <Link to={to} className={className} {...props}>
      {children}
    </Link>
  );
};

// ─── Inline SVG Area Chart for demo sections ──────────────────────────────
function TinyAreaChart({
  color = '#10b981',
  data = [30, 52, 41, 68, 55, 78, 65, 90, 72, 88, 76, 95],
}) {
  const w = 240,
    h = 60;
  const max = Math.max(...data),
    min = Math.min(...data) - 5;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = h - ((v - min) / (max - min)) * (h - 4) - 2;
    return `${x},${y}`;
  });
  const line = `M ${pts.join(' L ')}`;
  const area = `M 0,${h} L ${pts.join(' L ')} L ${w},${h} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" height="100%" preserveAspectRatio="none">
      <defs>
        <linearGradient id={`g-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#g-${color.replace('#', '')})`} />
      <path
        d={line}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ─── Product Feature Demo Mockup ──────────────────────────────────────────
function FeatureMockup({ tag, title, desc, icon: Icon, accentColor, mockupContent }) {
  return (
    <div className="grid md:grid-cols-2 gap-10 items-center">
      <div>
        <div
          className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold tracking-widest uppercase mb-4 border"
          style={{
            color: accentColor,
            borderColor: `${accentColor}30`,
            background: `${accentColor}10`,
          }}
        >
          <Icon size={11} /> {tag}
        </div>
        <h3 className="text-2xl md:text-3xl font-bold text-white mb-4 leading-snug">{title}</h3>
        <p className="text-gray-400 text-[15px] leading-relaxed">{desc}</p>
      </div>
      <div className="rounded-2xl border border-white/8 bg-[#0f0f0f] overflow-hidden shadow-2xl">
        <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-white/5 bg-black/30">
          <div className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/70" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-500/70" />
          <span className="ml-3 text-[10px] text-gray-600 font-medium">fitxero.com/admin/…</span>
        </div>
        <div className="p-5">{mockupContent}</div>
      </div>
    </div>
  );
}

// ─── Member Mockup Content ────────────────────────────────────────────────
function MembersMockup() {
  const members = [
    { name: 'Rahul Sharma', plan: 'Pro Monthly', status: 'Active', date: 'Jan 2026' },
    { name: 'Priya Mehta', plan: 'Starter', status: 'Active', date: 'Feb 2026' },
    { name: 'Amit Kumar', plan: 'Pro Monthly', status: 'Overdue', date: 'Nov 2025' },
    { name: 'Sneha Patel', plan: 'Starter', status: 'Active', date: 'Mar 2026' },
  ];
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-bold text-white">All Members</span>
        <span className="text-xs text-gray-500 bg-white/5 px-2 py-1 rounded-md">4 of 128</span>
      </div>
      <div className="space-y-2">
        {members.map((m, i) => (
          <div
            key={i}
            className="flex items-center justify-between rounded-xl bg-white/3 border border-white/5 px-3 py-2.5"
          >
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0">
                {m.name
                  .split(' ')
                  .map(w => w[0])
                  .join('')}
              </div>
              <div>
                <div className="text-xs font-semibold text-white">{m.name}</div>
                <div className="text-[10px] text-gray-500">
                  {m.plan} · {m.date}
                </div>
              </div>
            </div>
            <span
              className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${m.status === 'Active' ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}
            >
              {m.status}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Payments Mockup Content ──────────────────────────────────────────────
function PaymentsMockup() {
  const payments = [
    { name: 'Rahul Sharma', amount: '$45', status: 'Paid', date: 'Mar 21' },
    { name: 'Priya Mehta', amount: '$25', status: 'Paid', date: 'Mar 20' },
    { name: 'Amit Kumar', amount: '$45', status: 'Overdue', date: 'Feb 10' },
    { name: 'Dev Anand', amount: '$25', status: 'Pending', date: 'Mar 18' },
  ];
  return (
    <div>
      <div className="flex gap-3 mb-4">
        <div className="flex-1 bg-green-500/10 border border-green-500/20 rounded-xl p-3 text-center">
          <div className="text-lg font-black text-green-400">$18,450</div>
          <div className="text-[9px] text-gray-500 uppercase tracking-widest font-bold mt-0.5">
            Collected
          </div>
        </div>
        <div className="flex-1 bg-red-500/10 border border-red-500/20 rounded-xl p-3 text-center">
          <div className="text-lg font-black text-red-400">$2,250</div>
          <div className="text-[9px] text-gray-500 uppercase tracking-widest font-bold mt-0.5">
            Overdue
          </div>
        </div>
      </div>
      <div className="space-y-2">
        {payments.map((p, i) => (
          <div
            key={i}
            className="flex items-center justify-between rounded-xl bg-white/3 border border-white/5 px-3 py-2"
          >
            <div className="text-xs font-medium text-gray-300">{p.name}</div>
            <div className="flex items-center gap-3">
              <span className="text-xs font-bold text-white">{p.amount}</span>
              <span
                className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${p.status === 'Paid' ? 'bg-green-500/10 text-green-400 border border-green-500/20' : p.status === 'Overdue' ? 'bg-red-500/10 text-red-400 border border-red-500/20' : 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20'}`}
              >
                {p.status}
              </span>
              <span className="text-[10px] text-gray-600">{p.date}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Analytics Mockup ─────────────────────────────────────────────────────
function AnalyticsMockup() {
  return (
    <div>
      <div className="flex gap-3 mb-4">
        {[
          { v: '842', l: 'Members', c: '#10b981' },
          { v: '+15%', l: 'Growth', c: '#34d399' },
          { v: '94%', l: 'Retention', c: '#f97316' },
        ].map((s, i) => (
          <div
            key={i}
            className="flex-1 rounded-xl p-3 border text-center"
            style={{ background: `${s.c}10`, borderColor: `${s.c}25` }}
          >
            <div className="text-base font-black" style={{ color: s.c }}>
              {s.v}
            </div>
            <div className="text-[9px] text-gray-500 uppercase tracking-widest font-bold mt-0.5">
              {s.l}
            </div>
          </div>
        ))}
      </div>
      <div className="rounded-xl bg-white/3 border border-white/5 p-3">
        <div className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-2">
          Member Activity · 12 months
        </div>
        <div style={{ height: 70 }}>
          <TinyAreaChart color="#10b981" />
        </div>
      </div>
      <div className="mt-3 rounded-xl bg-white/3 border border-white/5 p-3">
        <div className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-2">
          Revenue Trend
        </div>
        <div style={{ height: 60 }}>
          <TinyAreaChart color="#22c55e" data={[40, 38, 55, 60, 52, 72, 68, 80, 75, 88, 82, 95]} />
        </div>
      </div>
    </div>
  );
}

// ─── Trainer Mockup ───────────────────────────────────────────────────────
function TrainersMockup() {
  const trainers = [
    { name: 'Vikram Singh', spec: 'Strength & Conditioning', members: 24, days: 'Mon–Fri' },
    { name: 'Anita Roy', spec: 'Yoga & Flexibility', members: 18, days: 'Mon–Wed–Fri' },
    { name: 'Rohan Das', spec: 'CrossFit & HIIT', members: 31, days: 'Tue–Thu–Sat' },
  ];
  return (
    <div className="space-y-3">
      {trainers.map((t, i) => (
        <div
          key={i}
          className="flex items-center gap-3 rounded-xl bg-white/3 border border-white/5 px-3 py-3"
        >
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-[11px] font-black text-white flex-shrink-0">
            {t.name
              .split(' ')
              .map(w => w[0])
              .join('')}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold text-white">{t.name}</div>
            <div className="text-[10px] text-gray-500 truncate">{t.spec}</div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-xs font-bold text-emerald-400">{t.members} members</div>
            <div className="text-[10px] text-gray-600">{t.days}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────
const LandingPage = () => {
  // Auth redirect is handled by App.js SmartLanding — this component
  // only renders for unauthenticated users. Pure presentation.
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      q: 'Do I need a credit card to start?',
      a: 'No — your 7-day free trial starts immediately with no payment required whatsoever. Cancel or upgrade any time.',
    },
    {
      q: 'Can I manage multiple gym locations?',
      a: 'Yes! The Pro plan supports multi-location management with separate dashboards, trainers, and member pools under one account.',
    },
    {
      q: 'Can my staff use Fitxero too?',
      a: 'Absolutely. You can add trainer accounts and admin accounts so your team can manage their sections independently.',
    },
    {
      q: 'What happens when my trial ends?',
      a: "We'll remind you 2 days before your trial ends. If you don't upgrade, your data stays safe and you can resume anytime.",
    },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white font-sans selection:bg-white/20 overflow-x-hidden">
      {/* ── Navigation ─────────────────────────────────────────────────── */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-white/5 bg-[#0a0a0a]/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="text-xl font-bold tracking-tight">
            Fitxero<span className="text-emerald-500">.</span>
          </div>
          <div className="hidden md:flex items-center space-x-8 text-sm text-gray-400">
            <a href="#how-it-works" className="hover:text-white transition-colors">
              How It Works
            </a>
            <a href="#features" className="hover:text-white transition-colors">
              Why Fitxero?
            </a>
            <a href="#pricing" className="hover:text-white transition-colors">
              Pricing
            </a>
            <a href="#faq" className="hover:text-white transition-colors">
              FAQ
            </a>
          </div>
          <div className="flex items-center space-x-4">
            <SmartLink
              to="/login"
              className="text-sm font-semibold text-gray-300 hover:text-white transition-colors"
            >
              Log In
            </SmartLink>
            <SmartLink
              to="/register"
              className="text-sm bg-emerald-500 text-black px-5 py-2.5 rounded-full font-bold hover:bg-emerald-400 transition-colors shadow-[0_0_15px_rgba(16,185,129,0.25)]"
            >
              Get Started
            </SmartLink>
          </div>
        </div>
      </nav>

      {/* ── Hero ───────────────────────────────────────────────────────── */}
      <section className="relative pt-32 pb-20 md:pt-48 md:pb-32 px-6 overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-emerald-500/10 blur-[120px] rounded-full pointer-events-none" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center space-x-2 bg-emerald-500/5 border border-emerald-500/20 rounded-full px-3 py-1 mb-8">
            <span className="flex h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-xs font-bold tracking-widest uppercase text-emerald-400">
              Trusted by Growing Gyms
            </span>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-8 leading-tight">
            Take Control of Your <br className="hidden md:block" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-500">
              Gym Business
            </span>
          </h1>
          <p className="text-lg md:text-xl text-gray-400 mb-10 max-w-2xl mx-auto leading-relaxed">
            Fitxero replaces your spreadsheets and notebooks with one beautifully designed platform.
            Manage members, trainers, payments, and analytics — all from a single dashboard.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <SmartLink
              to="/register"
              className="w-full sm:w-auto px-8 py-4 bg-emerald-500 text-black rounded-full font-bold text-lg hover:bg-emerald-400 transition-colors flex items-center justify-center shadow-[0_0_20px_rgba(16,185,129,0.3)]"
            >
              Start Free Trial <ArrowRight className="ml-2 w-5 h-5" />
            </SmartLink>
            <div className="flex items-center space-x-2 text-sm text-gray-400 mt-4 sm:mt-0">
              <span>Used by gym owners worldwide</span>
            </div>
          </div>
        </div>

        {/* Dashboard Mockup */}
        <div className="mt-20 md:mt-28 max-w-6xl mx-auto relative hidden sm:block">
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/10 via-teal-500/10 to-emerald-500/10 blur-3xl opacity-60 rounded-[3rem] pointer-events-none" />
          <div className="relative rounded-2xl border border-white/10 bg-[#0a0a0a] shadow-[0_8px_40px_rgba(0,0,0,0.8)] overflow-hidden ring-1 ring-white/5">
            <div className="h-10 bg-[#060606] border-b border-white/5 flex items-center px-4 relative">
              <div className="flex gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500/80" />
                <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                <div className="w-3 h-3 rounded-full bg-emerald-500/80" />
              </div>
              <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-2 bg-white/5 rounded-md px-3 py-1 border border-white/5">
                <Shield size={12} className="text-emerald-500" />
                <span className="text-xs font-medium text-gray-500">
                  fitxero.com/admin/dashboard
                </span>
              </div>
            </div>
            <div className="flex h-[500px] pointer-events-none select-none">
              <div className="w-[220px] bg-[#060606] border-r border-white/5 flex flex-col pt-5 px-3">
                <div className="flex items-center px-1 mb-8 gap-3">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-400 to-teal-600 flex items-center justify-center shadow-[0_0_15px_rgba(16,185,129,0.4)]">
                    <Dumbbell size={16} className="text-white" />
                  </div>
                  <div>
                    <div className="text-[14px] font-black text-white">Powerfit</div>
                    <div className="text-[9px] text-emerald-500 font-bold tracking-wider uppercase">
                      SMART GYM OS
                    </div>
                  </div>
                </div>
                <div className="space-y-1">
                  {[
                    { icon: LayoutDashboard, label: 'Dashboard', active: true },
                    { icon: Users, label: 'Members' },
                    { icon: UserCog, label: 'Trainers' },
                    { icon: Check, label: 'Attendance' },
                    { icon: CreditCard, label: 'Payments' },
                    { icon: BarChart2, label: 'Reports' },
                  ].map(({ icon: Icon, label, active }) => (
                    <div
                      key={label}
                      className={`flex items-center gap-3 px-3 py-2 rounded-xl font-semibold ${active ? 'bg-emerald-500 text-black shadow-lg shadow-emerald-500/20' : 'text-gray-500'}`}
                    >
                      <Icon size={15} /> <span className="text-sm">{label}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-auto mb-4 px-2">
                  <div className="flex items-center gap-3 p-2 rounded-xl bg-white/5 border border-white/5">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-orange-400 to-orange-600 flex items-center justify-center text-xs font-bold text-white">
                      KT
                    </div>
                    <div>
                      <p className="text-xs font-bold text-white">Kunal T.</p>
                      <p className="text-[10px] text-gray-400">Gym Owner</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex-1 bg-[#0a0a0a] flex flex-col">
                <div className="h-16 border-b border-white/5 flex items-center justify-between px-6">
                  <div className="text-2xl font-black text-white">Dashboard</div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-white/10 bg-[#0f0f0f] text-gray-500 w-48">
                      <Search size={14} />
                      <span className="text-xs">Search...</span>
                    </div>
                    <div className="w-8 h-8 rounded-xl border border-white/10 flex items-center justify-center text-gray-500">
                      <Bell size={14} />
                    </div>
                  </div>
                </div>
                <div className="p-6 flex flex-col gap-5 overflow-hidden">
                  <div className="grid grid-cols-4 gap-4">
                    {[
                      { n: '842', l: 'Active Members', c: 'blue', icon: Users },
                      { n: '$18,450', l: 'Monthly Revenue', c: 'green', icon: CreditCard },
                      { n: '15%', l: 'Growth Rate', c: 'orange', icon: TrendingUp },
                      { n: '342', l: "Today's Check-ins", c: 'purple', icon: Calendar },
                    ].map(({ n, l, c, icon: Icon }) => (
                      <div
                        key={l}
                        className={`bg-[#0f0f0f] border border-${c}-500/30 rounded-xl p-4 relative`}
                      >
                        <div
                          className={`absolute top-0 inset-x-0 h-[2px] bg-${c}-500 rounded-t-xl opacity-70`}
                        />
                        <div className="flex justify-between items-start mb-3">
                          <div
                            className={`w-8 h-8 rounded-lg bg-${c}-500/10 flex items-center justify-center`}
                          >
                            <Icon size={16} className={`text-${c}-500`} />
                          </div>
                          <div className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-green-500/10 text-green-500 border border-green-500/20">
                            ~ Live
                          </div>
                        </div>
                        <div className="text-2xl font-bold text-white mb-0.5">{n}</div>
                        <div className="text-[9px] font-bold text-gray-500 uppercase tracking-widest">
                          {l}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex-1 bg-[#0f0f0f] border border-white/10 rounded-xl p-5 overflow-hidden flex flex-col">
                    <div className="flex justify-between items-center mb-4">
                      <div className="flex items-center gap-2 text-sm font-bold text-white">
                        <Activity size={16} className="text-emerald-500" /> Member Activity Overview
                      </div>
                    </div>
                    <div className="flex-1 flex items-end justify-between gap-2 px-2 pb-1">
                      {[0.4, 0.6, 0.5, 0.9, 0.7, 0.8, 0.6, 0.95, 0.75, 0.6, 0.8, 0.5, 0.7].map(
                        (h, i) => (
                          <div
                            key={i}
                            className="w-full bg-gradient-to-t from-emerald-600/20 to-emerald-500/80 rounded-t-md"
                            style={{ height: `${h * 100}%` }}
                          />
                        )
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute bottom-0 inset-x-0 h-24 bg-gradient-to-t from-[#0a0a0a] to-transparent" />
          </div>
        </div>
      </section>

      {/* ── Trust Badges ───────────────────────────────────────────────── */}
      <section className="py-10 px-6 border-y border-white/5 bg-black/50">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            {[
              { icon: Lock, text: 'Bank-Grade Security' },
              { icon: Globe, text: 'Accessible Anywhere' },
              { icon: Smartphone, text: 'Mobile Friendly' },
              { icon: Headphones, text: 'Dedicated Support' },
              { icon: Clock, text: '99.9% Uptime' },
            ].map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-2 text-sm text-gray-400">
                <Icon size={15} className="text-gray-500" />
                <span className="font-medium">{text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How It Helps: 4 Benefit Cards ─────────────────────────────── */}
      <section id="how-it-works" className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-3 py-1 text-xs font-bold text-emerald-400 tracking-widest uppercase mb-5">
              How Fitxero Helps
            </div>
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              Everything your gym needs.
              <br />
              <span className="text-gray-500">Nothing it doesn't.</span>
            </h2>
            <p className="text-gray-400 max-w-xl mx-auto">
              From day one, Fitxero gives you a complete digital toolkit purpose-built for gym
              owners — not accountants, not developers.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              {
                icon: Users,
                color: '#10b981',
                title: 'Manage Members Easily',
                desc: 'Keep all your gym members in one organized dashboard. Add, edit, and track membership status in seconds.',
              },
              {
                icon: CreditCard,
                color: '#34d399',
                title: 'Track Payments Automatically',
                desc: "Never chase down dues again. Fitxero shows you who's paid, who hasn't, and how much you've earned this month.",
              },
              {
                icon: UserCog,
                color: '#059669',
                title: 'Simple Trainer Management',
                desc: 'Assign trainers, set their schedules, and see how many members they handle — all from a clean interface.',
              },
              {
                icon: BarChart2,
                color: '#10b981',
                title: 'Real Business Insights',
                desc: "Understand your gym's growth with live analytics, revenue trends, attendance charts, and retention rates.",
              },
            ].map(({ icon: Icon, color, title, desc }) => (
              <div
                key={title}
                className="bg-[#0a0a0a] border border-white/5 p-7 rounded-2xl hover:border-white/15 transition-all group hover:-translate-y-1 duration-200"
              >
                <div
                  className="w-12 h-12 rounded-xl mb-5 flex items-center justify-center"
                  style={{ background: `${color}15` }}
                >
                  <Icon size={22} style={{ color }} />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Product Feature Demo Sections ─────────────────────────────── */}
      <section id="features" className="py-12 px-6 bg-black/40 border-y border-white/5">
        <div className="max-w-6xl mx-auto space-y-24">
          <div className="text-center mb-2">
            <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-3 py-1 text-xs font-bold text-gray-300 tracking-widest uppercase mb-5">
              Inside the Product
            </div>
            <h2 className="text-3xl md:text-5xl font-bold">See exactly what you get.</h2>
            <p className="text-gray-400 mt-3 max-w-lg mx-auto">
              Fitxero is built around the real problems gym owners face every day. Here's what's
              inside.
            </p>
          </div>

          <FeatureMockup
            tag="Member Management"
            icon={Users}
            accentColor="#10b981"
            title="Add and manage gym members in seconds."
            desc="Every member gets a detailed profile with their membership plan, payment history, attendance record, trainer assignment, and personal notes. Filter, search, and export in one click."
            mockupContent={<MembersMockup />}
          />

          <div className="md:grid md:grid-cols-2 gap-10 items-center flex flex-col-reverse">
            <div className="rounded-2xl border border-white/8 bg-[#0f0f0f] overflow-hidden shadow-2xl">
              <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-white/5 bg-black/30">
                <div className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
                <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/70" />
                <div className="w-2.5 h-2.5 rounded-full bg-green-500/70" />
                <span className="ml-3 text-[10px] text-gray-600 font-medium">
                  fitxero.com/admin/payments
                </span>
              </div>
              <div className="p-5">
                <PaymentsMockup />
              </div>
            </div>
            <div>
              <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold tracking-widest uppercase mb-4 border border-green-500/30 bg-green-500/10 text-green-400">
                <CreditCard size={11} /> Payment Tracking
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4 leading-snug">
                See who paid and who hasn't. Always.
              </h3>
              <p className="text-gray-400 text-[15px] leading-relaxed">
                Fitxero keeps your payment records completely organized. View collected vs overdue
                amounts at a glance. Mark payments, send reminders, and generate monthly revenue
                summaries — without touching a spreadsheet.
              </p>
            </div>
          </div>

          <FeatureMockup
            tag="Trainer Management"
            icon={UserCog}
            accentColor="#10b981"
            title="Assign trainers and track schedules effortlessly."
            desc="Add your training staff, assign them to members, set their working days and hours, and see their full member rosters at a glance. No scheduling confusion. No missed sessions."
            mockupContent={<TrainersMockup />}
          />

          <div className="md:grid md:grid-cols-2 gap-10 items-center flex flex-col-reverse">
            <div className="rounded-2xl border border-white/8 bg-[#0f0f0f] overflow-hidden shadow-2xl">
              <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-white/5 bg-black/30">
                <div className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
                <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/70" />
                <div className="w-2.5 h-2.5 rounded-full bg-green-500/70" />
                <span className="ml-3 text-[10px] text-gray-600 font-medium">
                  fitxero.com/admin/reports
                </span>
              </div>
              <div className="p-5">
                <AnalyticsMockup />
              </div>
            </div>
            <div>
              <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold tracking-widest uppercase mb-4 border border-emerald-500/30 bg-emerald-500/10 text-emerald-400">
                <BarChart2 size={11} /> Analytics & Reports
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4 leading-snug">
                Understand your gym's performance with real data.
              </h3>
              <p className="text-gray-400 text-[15px] leading-relaxed">
                Track member growth, revenue trends, attendance patterns, and retention rates over
                time. Every chart updates live. Export reports as PDFs or CSVs to share with your
                partners or accountant.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Why Gym Owners Choose Fitxero (vs Manual) ─────────────────── */}
      <section className="py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-full px-3 py-1 text-xs font-bold text-red-400 tracking-widest uppercase mb-5">
              Why Switch to Fitxero?
            </div>
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              Stop managing your gym the hard way.
            </h2>
            <p className="text-gray-400 max-w-xl mx-auto">
              Most gym owners still rely on spreadsheets, notebooks, and WhatsApp reminders. Fitxero
              replaces all of that with a single, powerful platform.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Before column */}
            <div className="bg-red-500/5 border border-red-500/15 rounded-2xl p-8">
              <h3 className="text-lg font-bold text-red-400 mb-6 flex items-center gap-2">
                <X size={18} /> Without Fitxero
              </h3>
              <div className="space-y-4">
                {[
                  { icon: FileSpreadsheet, text: 'Member data scattered across Excel sheets' },
                  { icon: BookOpen, text: 'Payment tracking in notebooks & sticky notes' },
                  { icon: Clock, text: 'Hours spent chasing overdue payments manually' },
                  { icon: X, text: 'No visibility into gym performance or growth' },
                  { icon: X, text: 'Trainer assignments managed via WhatsApp' },
                  { icon: X, text: 'Attendance tracked on paper registers' },
                ].map(({ icon: Icon, text }, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-red-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <X size={10} className="text-red-400" />
                    </div>
                    <span className="text-sm text-gray-400">{text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* After column */}
            <div className="bg-green-500/5 border border-green-500/15 rounded-2xl p-8">
              <h3 className="text-lg font-bold text-green-400 mb-6 flex items-center gap-2">
                <Check size={18} /> With Fitxero
              </h3>
              <div className="space-y-4">
                {[
                  'All member profiles in one clean dashboard',
                  'Payment status visible with one click',
                  'Automated overdue tracking — no chasing needed',
                  'Live revenue and growth analytics always on',
                  'Trainer assignments with schedules and member lists',
                  'Digital attendance — fast, accurate, searchable',
                ].map((text, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-green-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check size={10} className="text-green-400" />
                    </div>
                    <span className="text-sm text-gray-300">{text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── How It Works — 3 Steps ─────────────────────────────────────── */}
      <section className="py-24 px-6 bg-black/40 border-y border-white/5">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-purple-500/10 border border-purple-500/20 rounded-full px-3 py-1 text-xs font-bold text-purple-400 tracking-widest uppercase mb-6">
            Simple Setup
          </div>
          <h2 className="text-3xl md:text-5xl font-bold mb-4">Ready in under 3 minutes.</h2>
          <p className="text-gray-400 mb-16 max-w-lg mx-auto">
            Getting started with Fitxero is as simple as three steps. No IT support. No training
            needed.
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                icon: Dumbbell,
                color: '#3b82f6',
                title: 'Create Your Gym Account',
                desc: "Register with your gym name, email, and a password. Your gym's dedicated dashboard is ready instantly.",
              },
              {
                step: '02',
                icon: Users,
                color: '#22c55e',
                title: 'Add Members & Trainers',
                desc: 'Import or manually add your members with their plans, contact details, and trainer assignments.',
              },
              {
                step: '03',
                icon: TrendingUp,
                color: '#a855f7',
                title: 'Track Everything Live',
                desc: 'Start tracking attendance, payments, reports, and growth. Your gym runs itself — you just watch the numbers grow.',
              },
            ].map(({ step, icon: Icon, color, title, desc }) => (
              <div key={step} className="relative">
                <div className="flex items-center justify-center mb-6">
                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg"
                    style={{ background: `${color}15`, border: `1px solid ${color}30` }}
                  >
                    <Icon size={28} style={{ color }} />
                  </div>
                </div>
                <div className="text-xs font-black text-gray-600 mb-2 tracking-widest">
                  STEP {step}
                </div>
                <h3 className="text-lg font-bold text-white mb-3">{title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-12">
            <SmartLink
              to="/register"
              className="inline-flex items-center gap-2 px-8 py-4 bg-emerald-500 hover:bg-emerald-400 text-black rounded-full font-bold text-base shadow-[0_0_20px_rgba(16,185,129,0.3)] transition-all"
            >
              Start in 3 Minutes <ChevronRight size={18} />
            </SmartLink>
          </div>
        </div>
      </section>

      {/* ── Features Section (existing) ────────────────────────────────── */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold mb-4">Why Choose Fitxero?</h2>
            <p className="text-gray-400">
              Benefits designed to provide a seamless, secure, and accessible experience.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                icon: Users,
                title: 'Member Management',
                desc: 'Easily add, track, and manage all your gym members in one centralized, secure dashboard.',
              },
              {
                icon: Zap,
                title: 'Instant Attendance',
                desc: 'Log check-ins instantly and track member progress without delays using our lightning-fast interface.',
              },
              {
                icon: CreditCard,
                title: 'Seamless Payments',
                desc: 'Track revenue, manage plans, and handle subscriptions with automated payment tracking features.',
              },
              {
                icon: Shield,
                title: 'Premium Security',
                desc: 'Your data is protected with cutting-edge security protocols in a sleek, isolated multi-tenant environment.',
              },
            ].map(({ icon: Icon, title, desc }) => (
              <div
                key={title}
                className="bg-[#0a0a0a] border border-white/5 p-8 rounded-3xl hover:border-white/20 transition-colors group"
              >
                <div className="w-12 h-12 bg-white/5 rounded-xl flex items-center justify-center mb-6 group-hover:bg-white/10 transition-colors">
                  <Icon className="text-white w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold mb-3">{title}</h3>
                <p className="text-gray-400 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Testimonial strip ─────────────────────────────────────────── */}
      <section className="py-16 px-6 bg-black/40 border-y border-white/5">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-white">Real words from gym owners</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                quote:
                  '"Fitxero cut my admin time by 80%. I used to spend hours on spreadsheets. Now it\'s all automated."',
                name: 'Arjun M.',
                gym: 'Iron House Gym · Mumbai',
                img: 'https://randomuser.me/api/portraits/men/32.jpg',
              },
              {
                quote:
                  '"Finally a gym software that actually looks good and works even better. My members love the organized system."',
                name: 'Priya S.',
                gym: 'FlexFit Studio · Pune',
                img: 'https://randomuser.me/api/portraits/women/44.jpg',
              },
              {
                quote:
                  '"Payment tracking alone was worth it. I can now see in 1 second who owes me and how much."',
                name: 'Ravi K.',
                gym: 'PowerZone Gym · Delhi',
                img: 'https://randomuser.me/api/portraits/men/86.jpg',
              },
            ].map(({ quote, name, gym, img }) => (
              <div
                key={name}
                className="bg-[#0a0a0a] border border-white/5 p-6 rounded-2xl hover:border-white/15 transition-colors"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={13} className="text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-300 text-sm leading-relaxed mb-5">{quote}</p>
                <div className="flex items-center gap-3">
                  <img
                    src={img}
                    alt={name}
                    className="w-9 h-9 rounded-full object-cover border border-white/10"
                  />
                  <div>
                    <p className="text-white text-sm font-bold">{name}</p>
                    <p className="text-gray-500 text-xs">{gym}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing Section ────────────────────────────────────────────── */}
      <section id="pricing" className="py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Choose Your Plan</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Transparent pricing for every gym owner. Scale as you grow with no hidden fees.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="bg-[#0a0a0a] border border-white/5 p-8 rounded-[2rem] flex flex-col hover:border-white/20 transition-all relative">
              <h3 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400 mb-2">
                Free Trial
              </h3>
              <div className="mb-6">
                <span className="text-5xl font-bold">$0</span>
                <span className="text-gray-500">/ 7 days</span>
              </div>
              <p className="text-sm text-gray-400 mb-8 h-10">
                Perfect for exploring our premium gym software before committing.
              </p>
              <SmartLink
                to="/register"
                className="w-full py-3 px-4 bg-white/5 hover:bg-white/10 text-white rounded-full font-medium transition-colors text-center mb-8 border border-white/10"
              >
                Start Free Trial
              </SmartLink>
              <div className="space-y-4 flex-1">
                <p className="text-sm font-medium mb-4">Included in trial:</p>
                {[
                  'Full Platform Access',
                  'Test Member Enrollment',
                  'Dashboard & Analytics Preview',
                  'Mobile & Desktop Access',
                ].map(f => (
                  <div key={f} className="flex items-start space-x-3 text-sm text-gray-300">
                    <Check className="w-5 h-5 text-gray-500 shrink-0" />
                    <span>{f}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-[#0a0a0a] border border-white/5 p-8 rounded-[2rem] flex flex-col hover:border-white/20 transition-all">
              <h3 className="text-2xl font-medium text-white mb-2">Starter</h3>
              <div className="mb-6">
                <span className="text-5xl font-bold">$25</span>
                <span className="text-gray-500">/mo</span>
              </div>
              <p className="text-sm text-gray-400 mb-8 h-10">
                Essential tools for independent gyms and boutique studios.
              </p>
              <SmartLink
                to="/register"
                className="w-full py-3 px-4 bg-white/5 hover:bg-white/10 text-white rounded-full font-medium transition-colors text-center mb-8 border border-white/10"
              >
                Start Free Trial
              </SmartLink>
              <div className="space-y-4 flex-1">
                <p className="text-sm font-medium mb-4">Everything in Free, plus:</p>
                {[
                  'Up to 200 Members',
                  '1 Admin Account',
                  'Basic Dashboard Overview',
                  'Member Progress Tracking',
                  'Attendance & Payment Tracking',
                  'Basic Reports',
                ].map(f => (
                  <div key={f} className="flex items-start space-x-3 text-sm text-gray-300">
                    <Check className="w-5 h-5 text-gray-500 shrink-0" />
                    <span className="text-gray-300">{f}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-b from-white/5 to-[#0a0a0a] border border-white/20 p-8 rounded-[2rem] flex flex-col relative transform md:-translate-y-4 shadow-xl shadow-white/5">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-white text-black px-4 py-1 rounded-full text-xs font-bold tracking-wide uppercase">
                Most Popular
              </div>
              <h3 className="text-2xl font-medium text-white mb-2">Pro</h3>
              <div className="mb-6">
                <span className="text-5xl font-bold">$45</span>
                <span className="text-gray-500">/mo</span>
              </div>
              <p className="text-sm text-gray-400 mb-8 h-10">
                Advanced features for growing gyms and multi-location franchises.
              </p>
              <SmartLink
                to="/register"
                className="w-full py-3 px-4 bg-emerald-500 text-black hover:bg-emerald-400 rounded-full font-bold transition-all shadow-[0_0_15px_rgba(16,185,129,0.25)] text-center mb-8"
              >
                Start Free Trial
              </SmartLink>
              <div className="space-y-4 flex-1">
                <p className="text-sm font-medium mb-4">Everything in Starter, plus:</p>
                {[
                  'Unlimited Members',
                  'Trainer Access & Multiple Admins',
                  'Advanced Dashboard Analytics',
                  'Detailed & Exportable Reports',
                  'Member Progress Charts',
                  'Full Member Management System',
                ].map(f => (
                  <div key={f} className="flex items-start space-x-3 text-sm text-gray-300">
                    <Check className="w-5 h-5 text-white shrink-0" />
                    <span className="text-gray-300">{f}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ────────────────────────────────────────────────────────── */}
      <section id="faq" className="py-24 px-6 bg-black/40 border-y border-white/5">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-3">Frequently Asked Questions</h2>
            <p className="text-gray-400 text-sm">
              Everything gym owners ask us before getting started.
            </p>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className="bg-[#0a0a0a] border border-white/5 rounded-2xl overflow-hidden hover:border-white/10 transition-colors"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left"
                >
                  <span className="text-sm font-semibold text-white">{faq.q}</span>
                  <ChevronRight
                    size={16}
                    className={`text-gray-500 flex-shrink-0 transition-transform duration-200 ${openFaq === i ? 'rotate-90' : ''}`}
                  />
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5 text-sm text-gray-400 leading-relaxed border-t border-white/5 pt-3">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ──────────────────────────────────────────────────── */}
      <section className="py-32 px-6 relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-emerald-500/10 blur-[100px] rounded-full pointer-events-none" />
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-3 py-1 text-xs font-bold text-emerald-400 uppercase tracking-widest mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            No credit card required
          </div>
          <h2 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
            Start Managing Your Gym
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-500">
              the Smart Way.
            </span>
          </h2>
          <p className="text-gray-400 mb-10 text-lg max-w-xl mx-auto leading-relaxed">
            Join gym owners who replaced their spreadsheets with Fitxero. Set up in 3 minutes.
            Cancel anytime.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <SmartLink
              to="/register"
              className="inline-flex items-center gap-2 px-10 py-4 bg-emerald-500 text-black rounded-full font-bold text-lg hover:bg-emerald-400 transition-all shadow-[0_0_20px_rgba(16,185,129,0.3)]"
            >
              Start Free Trial <ArrowRight size={20} />
            </SmartLink>
            <SmartLink
              to="/login"
              className="text-emerald-500 hover:text-emerald-400 text-sm font-semibold transition-colors"
            >
              Already have an account? Sign in →
            </SmartLink>
          </div>
          <p className="text-gray-600 text-xs mt-6">
            7-day free trial · No credit card · Cancel anytime
          </p>
        </div>
      </section>

      {/* ── Footer ─────────────────────────────────────────────────────── */}
      <footer className="border-t border-white/5 bg-black py-10 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between text-sm text-gray-500">
          <div className="flex items-center gap-2 mb-4 md:mb-0">
            <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <Dumbbell size={12} className="text-white" />
            </div>
            <span className="font-bold text-gray-400">
              Fitxero<span className="text-green-500">.</span>
            </span>
            <span className="text-gray-700 ml-2">© 2026 · Built for modern gyms.</span>
          </div>
          <div className="flex space-x-6">
            <a href="mailto:support@fitxero.com" className="hover:text-white transition-colors">
              Contact Support
            </a>
            <a href="/privacy" className="hover:text-white transition-colors">
              Privacy Policy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
